import React, { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../../../services/api';
import {
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, AreaChart, Area
} from 'recharts';
import { Gift, Award, Clock, Zap, Target, Calendar, Filter } from 'lucide-react';
import { ACCENT, GREEN, BLUE, PURPLE, AMBER, TIER_COLORS, Spinner, KpiCard } from './shared';

const PERIOD_PRESETS = [
  { id: 'week', label: 'Settimana', days: 7 },
  { id: '30d', label: '30 giorni', days: 30 },
  { id: '90d', label: 'Trimestre', days: 90 },
  { id: 'year', label: 'Anno', days: 365 },
  { id: 'custom', label: 'Personalizzato', days: null },
];
const SOURCE_COLORS = {
  'Compleanno': '#E91E63', '2a visita mese': '#FF6D00', '10a visita anno': '#6A1B9A',
  'Challenge': '#2196F3', 'Manuale': '#3ECF8E', 'Altro': '#888',
};

export function RewardsAnalyticsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preset, setPreset] = useState('30d');
  const today = new Date().toISOString().slice(0, 10);
  const thirtyAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState(thirtyAgo);
  const [dateTo, setDateTo] = useState(today);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
      const res = await adminApi.get(`/admin/analytics/rewards?${params}`);
      setData(res.data);
    } catch {
      setData(null);
    } finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handlePreset = (p) => {
    setPreset(p.id);
    if (p.days) {
      const to = new Date(); const from = new Date(Date.now() - p.days * 86400000);
      setDateFrom(from.toISOString().slice(0, 10));
      setDateTo(to.toISOString().slice(0, 10));
    }
  };

  const kpi = data?.kpi || {};
  const bySrc = data?.by_source || [];
  const trend = data?.trend || [];
  const topR = data?.top_rewards || [];
  const byTier = data?.by_tier || [];
  const effectiveness = data?.source_effectiveness || [];
  const hasData = kpi.total > 0;

  return (
    <div className="space-y-6" data-testid="rewards-analytics-tab">
      <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm" data-testid="rewards-period-filter">
        <div className="flex items-center gap-2 mb-3"><Filter size={14} className="text-[#D44E20]" /><span className="text-xs text-[#64748B] font-medium">Periodo</span></div>
        <div className="flex flex-wrap gap-2 items-end">
          <div className="flex gap-1 bg-slate-100 p-1 rounded-xl">
            {PERIOD_PRESETS.map(p => (
              <button key={p.id} onClick={() => handlePreset(p)} data-testid={`rewards-period-${p.id}`}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
                  preset === p.id ? 'bg-white text-[#D44E20] shadow-sm' : 'text-[#64748B] hover:text-[#1A1A1A]'}`}>
                {p.label}
              </button>
            ))}
          </div>
          {preset === 'custom' && (
            <div className="flex gap-2 items-end ml-2">
              <div><label className="text-[10px] text-[#64748B] block mb-1">Da</label>
                <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} data-testid="rewards-date-from"
                  className="h-9 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm focus:border-[#D44E20] focus:outline-none" /></div>
              <div><label className="text-[10px] text-[#64748B] block mb-1">A</label>
                <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} data-testid="rewards-date-to"
                  className="h-9 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm focus:border-[#D44E20] focus:outline-none" /></div>
              <button onClick={fetchData} data-testid="rewards-apply-filter"
                className="h-9 px-4 bg-[#D44E20] text-white text-sm font-medium rounded-xl hover:opacity-90">Applica</button>
            </div>
          )}
        </div>
      </div>

      {loading ? <Spinner /> : !hasData ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-slate-100 shadow-sm">
          <Gift size={48} className="mx-auto text-gray-300 mb-4" />
          <p className="text-[#64748B] text-sm mb-1">Nessun premio accreditato nel periodo selezionato</p>
          <p className="text-[#aaa] text-xs">I dati appariranno quando le automazioni inizieranno ad accreditare premi ai clienti</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="rewards-kpis">
            <KpiCard label="Totale accreditati" value={kpi.total} icon={Gift} color={ACCENT} testid="rw-kpi-total" />
            <KpiCard label="Riscattati" value={kpi.redeemed} icon={Award} color={GREEN} testid="rw-kpi-redeemed" />
            <KpiCard label="Scaduti" value={kpi.expired} icon={Clock} color="#EF4444" testid="rw-kpi-expired" />
            <KpiCard label="Ancora attivi" value={kpi.available} icon={Zap} color={BLUE} testid="rw-kpi-available" />
            <KpiCard label="Tasso conversione" value={`${kpi.conversion_rate}%`} icon={Target} color={PURPLE} testid="rw-kpi-rate" />
            <KpiCard label="Tempo medio riscatto" value={`${kpi.avg_redeem_days}g`} icon={Calendar} color={AMBER} testid="rw-kpi-avg-days" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {bySrc.length > 0 && (
              <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm" data-testid="rewards-by-source">
                <h3 className="font-semibold text-[#1A1A1A] mb-1">Premi per sorgente</h3>
                <p className="text-[10px] text-[#64748B] mb-4">Quale automazione genera pi{'\u00F9'} engagement</p>
                <div className="flex items-center gap-4">
                  <ResponsiveContainer width="45%" height={180}>
                    <PieChart>
                      <Pie data={bySrc} dataKey="total" nameKey="label" cx="50%" cy="50%" outerRadius={70} innerRadius={35} paddingAngle={2}>
                        {bySrc.map((s, i) => <Cell key={i} fill={SOURCE_COLORS[s.label] || ACCENT} />)}
                      </Pie>
                      <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex-1 space-y-2.5">
                    {bySrc.map((s, i) => (
                      <div key={i}>
                        <div className="flex items-center justify-between text-sm mb-0.5">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: SOURCE_COLORS[s.label] || ACCENT }} />
                            <span className="text-[#64748B] text-xs">{s.label}</span>
                          </div>
                          <span className="font-bold text-[#1A1A1A] text-xs">{s.total} <span className="text-[#64748B] font-normal">({s.rate}%)</span></span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            {effectiveness.length > 0 && (
              <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm" data-testid="rewards-effectiveness">
                <h3 className="font-semibold text-[#1A1A1A] mb-1">Efficacia per strategia</h3>
                <p className="text-[10px] text-[#64748B] mb-4">Tasso di conversione: quanti premi vengono effettivamente riscattati</p>
                <div className="space-y-3">
                  {effectiveness.map((s, i) => (
                    <div key={i}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ background: SOURCE_COLORS[s.label] || ACCENT }} />
                          <span className="text-[#64748B]">{s.label}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-[10px] text-[#64748B]">{s.redeemed}/{s.total}</span>
                          <span className="font-bold text-sm tabular-nums" style={{ color: s.rate >= 50 ? GREEN : s.rate >= 25 ? AMBER : '#EF4444' }}>{s.rate}%</span>
                        </div>
                      </div>
                      <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{ width: `${Math.max(s.rate, 2)}%`, background: SOURCE_COLORS[s.label] || ACCENT }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {trend.length > 1 && (
            <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm" data-testid="rewards-trend">
              <h3 className="font-semibold text-[#1A1A1A] mb-1">Trend premi nel tempo</h3>
              <p className="text-[10px] text-[#64748B] mb-4">Premi accreditati vs riscattati</p>
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={trend} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gAccr" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={ACCENT} stopOpacity={0.3} /><stop offset="95%" stopColor={ACCENT} stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gRisc" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={GREEN} stopOpacity={0.3} /><stop offset="95%" stopColor={GREEN} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#888' }} />
                  <YAxis tick={{ fontSize: 10, fill: '#888' }} />
                  <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                  <Area type="monotone" dataKey="accreditati" stroke={ACCENT} fill="url(#gAccr)" strokeWidth={2} name="Accreditati" />
                  <Area type="monotone" dataKey="riscattati" stroke={GREEN} fill="url(#gRisc)" strokeWidth={2} name="Riscattati" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {topR.length > 0 && (
              <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm" data-testid="rewards-top-rewards">
                <h3 className="font-semibold text-[#1A1A1A] mb-1">Premi pi{'\u00F9'} popolari</h3>
                <p className="text-[10px] text-[#64748B] mb-4">Quali premi generano pi{'\u00F9'} interesse</p>
                {topR.length > 2 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={topR} layout="vertical" margin={{ top: 0, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 10, fill: '#888' }} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: '#666' }} width={120} />
                      <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                      <Bar dataKey="total" name="Accreditati" fill={ACCENT} radius={[0, 4, 4, 0]} barSize={16} />
                      <Bar dataKey="redeemed" name="Riscattati" fill={GREEN} radius={[0, 4, 4, 0]} barSize={16} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="space-y-3">
                    {topR.map((r, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ background: i === 0 ? ACCENT : BLUE }}>{i + 1}</div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-[#1A1A1A] truncate">{r.name}</p>
                          <p className="text-[10px] text-[#64748B]">{r.redeemed}/{r.total} riscattati ({r.rate}%)</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {byTier.length > 0 && (
              <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm" data-testid="rewards-by-tier">
                <h3 className="font-semibold text-[#1A1A1A] mb-1">Premi per tier cliente</h3>
                <p className="text-[10px] text-[#64748B] mb-4">Quali segmenti rispondono meglio alle automazioni</p>
                <div className="flex items-center gap-4">
                  <ResponsiveContainer width="40%" height={180}>
                    <PieChart>
                      <Pie data={byTier} dataKey="total" nameKey="tier" cx="50%" cy="50%" outerRadius={70} innerRadius={35} paddingAngle={2}>
                        {byTier.map(t => <Cell key={t.tier} fill={TIER_COLORS[t.tier] || '#888'} />)}
                      </Pie>
                      <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex-1 space-y-3">
                    {byTier.map((t, i) => (
                      <div key={i}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full" style={{ background: TIER_COLORS[t.tier] || '#888' }} />
                            <span className="text-[#64748B]">{t.tier}</span>
                          </div>
                          <div className="text-right">
                            <span className="font-bold text-[#1A1A1A] text-xs">{t.total}</span>
                            <span className="text-[10px] text-[#64748B] ml-1">({t.rate}% riscosso)</span>
                          </div>
                        </div>
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all" style={{ width: `${Math.max(t.rate, 2)}%`, background: TIER_COLORS[t.tier] || '#888' }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
